import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sparkles, FileText, ClipboardList, LayoutGrid, ShieldCheck } from "lucide-react";

const WORKSPACES = [
  { to: "/workspace/carer-starter", label: "Carer Starter Hub", desc: "Free carer members" },
  { to: "/workspace/family-starter", label: "Family Starter Hub", desc: "Free family members" },
  { to: "/workspace/pro-carer-circle", label: "Pro Carer Office", desc: "Paid carer members" },
  { to: "/workspace/family-circle", label: "Family Care Office", desc: "Paid family members" },
];

const TIER_LABELS = {
  free: "Free Carer Access",
  pro_carer: "Pro Carer Office",
  family_access: "Family Care Office — £19/month",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [search] = useSearchParams();
  const upgrade = search.get("upgrade");

  return (
    <div className="container-jcc py-16 md:py-24" data-testid="page-dashboard">
      <h1 className="font-display text-4xl md:text-5xl font-bold">Welcome, {user?.name?.split(' ')[0] || "there"}.</h1>
      <p className="mt-2 text-ink-muted">Signed in as <strong>{user?.email}</strong>.</p>

      <div className="mt-10 grid md:grid-cols-3 gap-6">
        <div className="card-base p-6">
          <div className="overline text-ink-soft">Your membership</div>
          <div className="mt-2 font-display text-2xl font-bold">{TIER_LABELS[user?.membership_tier] || "Free Carer Access"}</div>
          {user?.membership_expires_at && (
            <p className="mt-1 text-sm text-ink-muted">Active until {new Date(user.membership_expires_at).toLocaleDateString('en-GB')}</p>
          )}
          {user?.membership_tier === "free" && (
            <div className="mt-5 space-y-2">
              <a
                href="/membership/pro-carer-circle"
                className="btn-purple w-full"
              >
                Upgrade to Pro Carer
              </a>
              <a
                href="/membership/family-circle"
                className="btn-teal w-full"
              >
                Get Family Care Office
              </a>
            </div>
          )}
        </div>

        <div className="card-base p-6">
          <Sparkles className="w-6 h-6 text-teal" />
          <h3 className="mt-3 font-display text-xl font-semibold">Explore resources</h3>
          <p className="mt-2 text-sm text-ink-muted">Templates, checklists and guides for your situation.</p>
          <Link to="/resources" className="btn-outline mt-4 w-full">Resource Library</Link>
        </div>

        <div className="card-base p-6">
          <ClipboardList className="w-6 h-6 text-purple" />
          <h3 className="mt-3 font-display text-xl font-semibold">Shift Toolkit</h3>
          <p className="mt-2 text-sm text-ink-muted">On-shift essentials. Quick access to all the records you need.</p>
          <Link to="/shift-toolkit" className="btn-outline mt-4 w-full">Open toolkit</Link>
        </div>
      </div>

      {user?.role === "admin" && (
        <div className="mt-12" data-testid="dashboard-admin-tools">
          <div className="flex items-center gap-3 mb-5">
            <ShieldCheck className="w-5 h-5 text-teal" />
            <h2 className="font-display text-2xl font-semibold">Admin tools</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/admin/forms"
              className="card-base p-5 block focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
              data-testid="dashboard-admin-link-forms"
            >
              <div className="overline text-ink-soft">JCC Form Builder</div>
              <div className="mt-1 font-display text-lg font-semibold">Manage forms</div>
              <div className="mt-3 text-sm text-teal">Create, edit &amp; publish forms →</div>
            </Link>
            <Link
              to="/admin/care-needs"
              className="card-base p-5 block focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
              data-testid="dashboard-admin-link-care-needs"
            >
              <div className="overline text-ink-soft">Family submissions</div>
              <div className="mt-1 font-display text-lg font-semibold">Care Needs queue</div>
              <div className="mt-3 text-sm text-teal">Review &amp; approve →</div>
            </Link>
          </div>
        </div>
      )}

      <div className="mt-12">
        <div className="flex items-center gap-3 mb-5">
          <LayoutGrid className="w-5 h-5 text-purple" />
          <h2 className="font-display text-2xl font-semibold">Member workspaces</h2>
        </div>
        <p className="text-sm text-ink-muted max-w-2xl">
          These are the member-only workspace areas. Access is normally directed
          from your Systeme back office — links are listed here for convenience.
        </p>
        <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WORKSPACES.map((w) => (
            <Link
              key={w.to}
              to={w.to}
              className="card-base p-5 block focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
              data-testid={`dashboard-link-${w.to.split("/").pop()}`}
            >
              <div className="overline text-ink-soft">{w.desc}</div>
              <div className="mt-1 font-display text-lg font-semibold">{w.label}</div>
              <div className="mt-3 text-sm text-teal">Open workspace →</div>
            </Link>
          ))}
        </div>
      </div>

      {upgrade && (
        <div className="mt-10 rounded-2xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <FileText className="inline-block w-5 h-5 mr-2" /> That area is part of a paid plan. Pick an option above to unlock it.
        </div>
      )}
    </div>
  );
}
